import RPNInput, { isValidPhoneNumber } from 'react-phone-number-input';
import type { Country, Value } from 'react-phone-number-input';

interface PhoneInputProps {
  /** Current value, always canonical E.164 (e.g. "+441234567890"). Empty string for unset. */
  value: string;
  /** Called whenever the user changes the input. Always returns an E.164 string or empty string. */
  onChange: (value: string) => void;
  /** Default country shown before the user picks one. Defaults to GB. */
  defaultCountry?: Country;
  /** Optional id for the underlying input element (so a `<label htmlFor>` can target it). */
  id?: string;
  /** Optional placeholder for the number input. */
  placeholder?: string;
  /** Disable the whole control. */
  disabled?: boolean;
  /** Apply the invalid-state border treatment (caller owns the validation logic). */
  invalid?: boolean;
  /** Optional aria-describedby for the input element. */
  'aria-describedby'?: string;
}

/**
 * Phone input that always emits canonical E.164 (e.g. `+441234567890`).
 *
 * Wraps `react-phone-number-input` so the rest of the app sees a plain string
 * value/onChange instead of the library's `Value | undefined`. Defaults to GB
 * and always shows the international `+` prefix so users can't enter ambiguous
 * national-format numbers.
 *
 * Backed by `libphonenumber-js`. The matching server-side Zod refinement lives
 * in `server/src/utils/validation.ts` (`isValidE164Phone`).
 */
export function PhoneInput(props: PhoneInputProps) {
  return (
    <RPNInput
      international
      defaultCountry={props.defaultCountry ?? 'GB'}
      id={props.id}
      value={(props.value || undefined) as Value | undefined}
      onChange={(v) => props.onChange((v ?? '') as string)}
      placeholder={props.placeholder ?? 'Enter phone number'}
      disabled={props.disabled}
      aria-describedby={props['aria-describedby']}
      className={props.invalid ? 'PhoneInput PhoneInput--invalid' : 'PhoneInput'}
    />
  );
}

/**
 * Returns true when `value` is empty or a valid E.164 number.
 * Mirrors the server-side rule so callers can disable Save buttons until the
 * user enters something usable.
 */
export function isValidPhoneInputValue(value: string): boolean {
  if (value.length === 0) return true;
  return isValidPhoneNumber(value);
}
